'use client';

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FRED_SERIES, FRED_SECTIONS, type FredRange } from '@/lib/fred-series';
import { RangeSelector } from './RangeSelector';
import { FredChart, deriveRecessionRanges, type RecessionRange } from './FredChart';
import { ShillerPeChart } from './ShillerPeChart';
import { YieldCurveSnapshot } from './YieldCurveSnapshot';

export function EconomyTab() {
  const [range, setRange] = useState<FredRange>('5Y');

  const { data: usrec } = useQuery<{ observations: { date: string; value: number | null }[] }>({
    queryKey: ['/api/terminal/fred', 'USREC', 'Max'],
    queryFn: async () => {
      const res = await fetch('/api/terminal/fred/USREC?range=Max');
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    staleTime: 12 * 60 * 60 * 1000,
  });

  // Memoize so the array reference is stable across renders — otherwise every
  // FredChart's visibleRecessions useMemo gets invalidated on each parent re-render.
  const recessionRanges: RecessionRange[] = useMemo(
    () => (usrec ? deriveRecessionRanges(usrec.observations) : []),
    [usrec],
  );

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-10 -mx-4 px-4 py-2 bg-[#0A0E17]/95 backdrop-blur border-b border-[#1F2937] flex items-center justify-between">
        <div className="text-[11px] text-[#6B7280] font-mono">
          U.S. macro indicators · data:{' '}
          <a href="https://fred.stlouisfed.org/" target="_blank" rel="noopener" className="text-[var(--color-accent)] hover:underline">FRED</a>
          {' · '}
          <a href="https://www.multpl.com/shiller-pe" target="_blank" rel="noopener" className="text-[var(--color-accent)] hover:underline">Shiller CAPE</a>
          {' · gray bands = NBER recessions'}
        </div>
        <RangeSelector value={range} onChange={setRange} />
      </div>

      <section className="space-y-3">
        <h2 className="text-xs font-mono font-semibold tracking-wider uppercase text-[#9CA3AF]">
          Valuation
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ShillerPeChart range={range} recessionRanges={recessionRanges} />
        </div>
      </section>

      {FRED_SECTIONS.map((section) => {
        const seriesInSection = FRED_SERIES.filter((s) => s.section === section.id);
        return (
          <section key={section.id} className="space-y-3">
            <h2 className="text-xs font-mono font-semibold tracking-wider uppercase text-[#9CA3AF]">
              {section.label}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {section.id === 'rates' && <YieldCurveSnapshot />}
              {seriesInSection.map((s) => (
                <FredChart key={s.id} config={s} range={range} recessionRanges={recessionRanges} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
